import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Sun,
  Moon,
  ArrowLeft,
  Save,
  Monitor,
  Palette,
  Sparkles,
  Check,
  Laptop,
  Clock
} from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

// Supabase client initialization
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

const ThemePreferences = () => {
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  // Başlangıçta mevcut temaya göre seçim
  const [selectedTheme, setSelectedTheme] = useState(isDark ? 'dark' : 'light');
  const [autoTheme, setAutoTheme] = useState(false);
  const [themeSchedule, setThemeSchedule] = useState({
    enabled: false,
    lightStart: '06:00', // UI için tutuluyor, fakat zaman bazlı seçim darkStart üzerinden yapılıyor.
    darkStart: '18:00'
  });

  const themes = [
    {
      id: 'light',
      name: 'Aydınlık',
      icon: Sun,
      description: 'Klasik aydınlık tema',
      preview: {
        bg: 'bg-white',
        text: 'text-gray-900',
        accent: 'bg-blue-500'
      }
    },
    {
      id: 'dark',
      name: 'Karanlık',
      icon: Moon,
      description: 'Göz yormayan karanlık tema',
      preview: {
        bg: 'bg-gray-900',
        text: 'text-white',
        accent: 'bg-blue-600'
      }
    }
  ];

  // Tema tercihlerini kaydet
  const handleSave = async () => {
    try {
      setIsLoading(true);
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        navigate('/signin');
        return;
      }

      let finalTheme = selectedTheme;
      // Eğer Zaman Bazlı Tema aktifse, darkStart saatine göre temayı belirle
      if (themeSchedule.enabled) {
        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        const [darkHours, darkMinutes] = themeSchedule.darkStart.split(':').map(Number);
        const darkStartMinutes = darkHours * 60 + darkMinutes;
        finalTheme = currentMinutes >= darkStartMinutes ? 'dark' : 'light';
        if (finalTheme !== selectedTheme) {
          setSelectedTheme(finalTheme);
        }
      } else if (autoTheme) {
        // Eğer sistem teması aktifse, window.matchMedia ile belirle
        finalTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light';
        if (finalTheme !== selectedTheme) {
          setSelectedTheme(finalTheme);
        }
      }
      // Diğer durumlarda, kullanıcının manuel seçimi (selectedTheme) geçerli olur.

      // Önce mevcut kaydı kontrol et
      const { data: existingPrefs } = await supabase
        .from('theme_preferences')
        .select('id')
        .eq('user_id', session.user.id)
        .single();

      let updateError;

      if (existingPrefs) {
        // Kayıt varsa güncelle
        const { error } = await supabase
          .from('theme_preferences')
          .update({
            theme: finalTheme,
            auto_theme: autoTheme,
            theme_schedule: themeSchedule,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', session.user.id);

        updateError = error;
      } else {
        // Kayıt yoksa yeni kayıt oluştur
        const { error } = await supabase
          .from('theme_preferences')
          .insert([{
            user_id: session.user.id,
            theme: finalTheme,
            auto_theme: autoTheme,
            theme_schedule: themeSchedule,
            updated_at: new Date().toISOString()
          }]);

        updateError = error;
      }

      if (updateError) throw updateError;

      // Uygulamadaki tema, finalTheme ile uyuşmuyorsa temayı değiştir
      if ((isDark && finalTheme !== 'dark') || (!isDark && finalTheme !== 'light')) {
        toggleTheme();
      }

      toast.success('Tema tercihleri kaydedildi');
    } catch (error) {
      console.error('Error saving theme preferences:', error);
      toast.error('Tema tercihleri kaydedilirken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  // Kullanıcının tema tercihlerini yükle
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const { data: preferences, error } = await supabase
          .from('theme_preferences')
          .select('*')
          .eq('user_id', session.user.id)
          .single();

        if (error) {
          if (error.code === 'PGRST116') {
            // Kayıt bulunamadı, varsayılan değerleri kullan
            return;
          }
          throw error;
        }

        if (preferences) {
          setSelectedTheme(preferences.theme);
          setAutoTheme(preferences.auto_theme);
          setThemeSchedule(preferences.theme_schedule);
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
        toast.error('Tercihler yüklenirken bir hata oluştu');
      }
    };

    loadPreferences();
  }, []);

  // Sistem temasını takip et (yalnızca autoTheme aktifse çalışsın)
  useEffect(() => {
    if (!autoTheme) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const newTheme = e.matches ? 'dark' : 'light';
      if (newTheme !== selectedTheme) {
        setSelectedTheme(newTheme);
        if ((isDark && newTheme === 'light') || (!isDark && newTheme === 'dark')) {
          toggleTheme();
        }
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [autoTheme, isDark, selectedTheme, toggleTheme]);

  // Zaman Bazlı Tema: periyodik kontrol, darkStart saatine göre temayı ayarla
  useEffect(() => {
    if (!themeSchedule.enabled) return;

    const updateThemeByTime = () => {
      const now = new Date();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const [darkHours, darkMinutes] = themeSchedule.darkStart.split(':').map(Number);
      const darkStartMinutes = darkHours * 60 + darkMinutes;
      const newTheme = currentMinutes >= darkStartMinutes ? 'dark' : 'light';

      if (newTheme !== selectedTheme) {
        setSelectedTheme(newTheme);
        if ((isDark && newTheme === 'light') || (!isDark && newTheme === 'dark')) {
          toggleTheme();
        }
      }
    };

    // İlk kontrol
    updateThemeByTime();
    // Her dakika kontrol et
    const interval = setInterval(updateThemeByTime, 60000);
    return () => clearInterval(interval);
  }, [themeSchedule, selectedTheme, isDark, toggleTheme]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Navbar */}
      <div className="sticky top-0 z-10 bg-white/80 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              Görünüm Tercihleri
            </h1>
            <div className="flex items-center gap-4">
              {/* Geri Dön */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(-1)}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg
                  ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
                  transition-colors
                `}
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Geri Dön</span>
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* Ana İçerik */}
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Üst Başlık */}
          <div className="mb-8">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Görünüm Ayarları
                </h2>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                  Uygulama temasını ve görünüm tercihlerinizi özelleştirin.
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSave}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-colors disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Kaydediliyor...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Değişiklikleri Kaydet</span>
                  </>
                )}
              </motion.button>
            </div>
          </div>

          <div className="space-y-6">
            {/* Tema Seçimi */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <Palette className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Tema Seçimi
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Tercih ettiğiniz temayı seçin
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {themes.map((theme) => (
                    <motion.button
                      key={theme.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedTheme(theme.id)}
                      className={`relative p-4 rounded-xl border-2 transition-colors ${
                        selectedTheme === theme.id
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700'
                      }`}
                    >
                      {selectedTheme === theme.id && (
                        <div className="absolute top-2 right-2">
                          <Check className="w-5 h-5 text-primary-500" />
                        </div>
                      )}
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${theme.id === 'dark' ? 'bg-gray-800' : 'bg-white'} border border-gray-200 dark:border-gray-700`}>
                          <theme.icon className={`w-6 h-6 ${theme.id === 'dark' ? 'text-gray-100' : 'text-gray-900'}`} />
                        </div>
                        <div className="text-left">
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {theme.name}
                          </h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {theme.description}
                          </p>
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Otomatik Tema */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <Monitor className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Otomatik Tema
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Sistem temasına göre otomatik değişim
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Sistem Teması */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Laptop className="w-5 h-5 text-gray-400" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        Sistem Temasını Kullan
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Cihazınızın tema ayarlarına göre otomatik değişir
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (!autoTheme) {
                        setAutoTheme(true);
                        // Eğer zaman bazlı tema açıksa, kapat
                        if (themeSchedule.enabled) {
                          setThemeSchedule(prev => ({ ...prev, enabled: false }));
                        }
                      } else {
                        setAutoTheme(false);
                      }
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                      autoTheme ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <motion.span
                      initial={false}
                      animate={{
                        translateX: autoTheme ? '20px' : '2px'
                      }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      className="inline-block h-5 w-5 transform rounded-full bg-white"
                    />
                  </button>
                </div>

                {/* Zaman Bazlı Tema */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Clock className="w-5 h-5 text-gray-400" />
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                          Zaman Bazlı Tema
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Belirli saatlerde otomatik tema değişimi
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        if (!themeSchedule.enabled) {
                          setThemeSchedule(prev => ({ ...prev, enabled: true }));
                          // Eğer sistem teması açıksa, kapat
                          if (autoTheme) {
                            setAutoTheme(false);
                          }
                        } else {
                          setThemeSchedule(prev => ({ ...prev, enabled: false }));
                        }
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                        themeSchedule.enabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    >
                      <motion.span
                        initial={false}
                        animate={{
                          translateX: themeSchedule.enabled ? '20px' : '2px'
                        }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        className="inline-block h-5 w-5 transform rounded-full bg-white"
                      />
                    </button>
                  </div>

                  {themeSchedule.enabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Aydınlık Tema Başlangıcı
                        </label>
                        <input
                          type="time"
                          value={themeSchedule.lightStart}
                          onChange={(e) => setThemeSchedule(prev => ({
                            ...prev,
                            lightStart: e.target.value
                          }))}
                          className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Karanlık Tema Başlangıcı
                        </label>
                        <input
                          type="time"
                          value={themeSchedule.darkStart}
                          onChange={(e) => setThemeSchedule(prev => ({
                            ...prev,
                            darkStart: e.target.value
                          }))}
                          className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Bilgilendirme Kartı */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4"
            >
              <div className="flex gap-3">
                <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300">
                    Tema Ayarları Hakkında
                  </h4>
                  <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                    Tema tercihleriniz otomatik olarak kaydedilir ve tüm cihazlarınızda senkronize edilir.
                    Sistem teması veya zaman bazlı tema seçeneklerinden sadece birini etkinleştirebilirsiniz.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemePreferences;
